import React, { useCallback, useEffect, useState } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import { IntlProvider } from 'react-intl-next';
import { useDispatch, useSelector } from 'react-redux';

import { Grid, GridColumn } from '@atlaskit/page';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import {
  ContextualAnalyticsData,
  FireScreenAnalytics,
  SCREEN,
} from '@atlassian/analytics-bridge';

import { FocusedPageHeader } from 'src/components/accessibility';
import DeploymentDashboard from 'src/components/pipelines/components/deployment-dashboard';
import { ModalType } from 'src/components/pipelines/components/deployments-modal';
import { DeploymentsWebsocketEvents } from 'src/components/pipelines/constants';
import {
  requestEnvironmentHistory,
  requestRedeployment,
  setDeploymentPreview,
} from 'src/components/pipelines/redux/actions/deployments';
import {
  webSocketSubscribe,
  webSocketUnsubscribe,
} from 'src/components/pipelines/redux/actions/pipelines';
import {
  getDeploymentDashboard,
  getEnvironmentHistoryMap,
  getFetchingEnvironmentHistoryMap,
  isFetchingDeploymentDashboard,
} from 'src/components/pipelines/redux/selectors/deployments';
import { getRepository } from 'src/components/pipelines/redux/selectors/pipelines';
import { getDeploymentsSettingsPageURL } from 'src/components/pipelines/utils/links';
import { useHistory__UNSAFE, useParams } from 'src/router/hooks';
import Breadcrumbs from 'src/sections/repository/containers/breadcrumbs';
import { getUpToSmallBreakpointState } from 'src/selectors/global-selectors';
import { getIsPipelinesPremium } from 'src/selectors/repository-selectors';

import PreviewModal from './preview-modal';
import RedeployModal from './redeploy-modal';
import SummaryModal from './summary-modal';

// TODO - update component sizings after migration
const DashboardWrapper = styled.div({
  marginTop: '-8px',
  '& > div': {
    marginLeft: '20px',
  },
  "[data-testid='deployment-history']": {
    height: 'calc(100vh - 190px)',
  },
});

const DeploymentsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState<
    ModalType.PREVIEW | ModalType.SUMMARY | ModalType.REDEPLOY | false
  >(false);
  const dispatch = useDispatch();
  const history = useHistory__UNSAFE();
  const {
    repositoryFullSlug,
    deploymentUuid: selectedDeploymentUuid,
    environmentUuid: selectedEnvironmentUuid,
  } = useParams<{
    deploymentUuid: string;
    environmentUuid: string;
    repositoryFullSlug: string;
  }>();

  const isUpToSmallBreakpointActive = useSelector(getUpToSmallBreakpointState);
  const deploymentDashboard = useSelector(getDeploymentDashboard);
  const environmentHistory = useSelector(getEnvironmentHistoryMap);
  const isPipelinesPremium = useSelector(getIsPipelinesPremium);
  const repository = useSelector(getRepository);

  const isFetchingDashboard = useSelector(isFetchingDeploymentDashboard);
  const isFetchingHistory = useSelector(getFetchingEnvironmentHistoryMap)(
    selectedEnvironmentUuid
  );

  const deploymentSettingsPageUrl =
    getDeploymentsSettingsPageURL(repositoryFullSlug);

  useEffect(() => {
    DeploymentsWebsocketEvents.forEach(e => dispatch(webSocketSubscribe(e)));
    return () => {
      DeploymentsWebsocketEvents.forEach(e =>
        dispatch(webSocketUnsubscribe(e))
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDeploymentUuid && !isModalOpen) {
      setIsModalOpen(ModalType.SUMMARY);
    } else if (!selectedDeploymentUuid && isModalOpen === ModalType.SUMMARY) {
      setIsModalOpen(false);
    }
  }, [setIsModalOpen, isModalOpen, selectedDeploymentUuid]);

  const toggleHistory = useCallback(
    (environmentUuid: any) => {
      const newPath =
        selectedEnvironmentUuid === environmentUuid || !environmentUuid
          ? ''
          : `environments/${environmentUuid}`;
      history.push({
        pathname: `/${repositoryFullSlug}/deployments/${newPath}`,
        search: location.search,
      });
    },
    [history, selectedEnvironmentUuid, repositoryFullSlug]
  );

  const openDeploymentSummary = useCallback(
    (deploymentUuid: any) => {
      const environmentRoutePart = selectedEnvironmentUuid
        ? `environments/${selectedEnvironmentUuid}/`
        : '';
      const newPath = `${environmentRoutePart}${deploymentUuid}`;
      history.push({
        pathname: `/${repositoryFullSlug}/deployments/${newPath}`,
        search: location.search,
      });
    },
    [history, repositoryFullSlug, selectedEnvironmentUuid]
  );

  const openDeploymentPreview = useCallback(
    ({ deployment, environment }: any) => {
      dispatch(setDeploymentPreview(deployment, environment));
      setIsModalOpen(ModalType.PREVIEW);
    },
    [dispatch, setIsModalOpen]
  );

  const openRedeployment = useCallback(
    (deployment: any) => {
      dispatch(
        requestRedeployment(deployment.environmentUuid, deployment.uuid)
      );
      setIsModalOpen(ModalType.REDEPLOY);
    },
    [dispatch, setIsModalOpen]
  );

  const onCloseModal = useCallback(() => {
    const newPath = selectedEnvironmentUuid
      ? `environments/${selectedEnvironmentUuid}/`
      : '';
    history.push({
      pathname: `/${repositoryFullSlug}/deployments/${newPath}`,
      search: location.search,
    });
    setIsModalOpen(false);
  }, [history, repositoryFullSlug, selectedEnvironmentUuid]);

  const fetchEnvironmentHistory = useCallback(
    (environmentUuid: any, currentPage: any) => {
      dispatch(requestEnvironmentHistory(environmentUuid, currentPage));
    },
    [dispatch]
  );

  return (
    <ContextualAnalyticsData sourceName="deployments" sourceType={SCREEN}>
      <FireScreenAnalytics attributes={{ repositoryUuid: repository.uuid }} />
      <Helmet defer={false} title="Deployments" />
      <Grid
        layout="fluid"
        spacing={isUpToSmallBreakpointActive ? 'cosy' : 'comfortable'}
      >
        <GridColumn>
          <FocusedPageHeader breadcrumbs={<Breadcrumbs />}>
            Deployments
          </FocusedPageHeader>
          <IntlProvider locale="en">
            <DashboardWrapper>
              <UFOLoadHold
                name="deployment-dashboard-loading"
                hold={isFetchingDashboard}
              />
              <DeploymentDashboard
                deploymentDashboard={deploymentDashboard}
                environmentHistory={environmentHistory}
                fetchingDashboard={isFetchingDashboard}
                fetchingHistory={isFetchingHistory && selectedEnvironmentUuid}
                selectedEnvironmentUuid={selectedEnvironmentUuid}
                deploymentSettingsPageUrl={deploymentSettingsPageUrl}
                userIsAdmin={repository?.userIsAdmin}
                isPremiumAccount={isPipelinesPremium}
                getEnvironmentHistory={fetchEnvironmentHistory}
                toggleHistory={toggleHistory}
                openDeploymentPreview={openDeploymentPreview}
                openDeploymentSummary={openDeploymentSummary}
                openRedeployPreview={openRedeployment}
              />
            </DashboardWrapper>
            {isModalOpen === ModalType.SUMMARY ? (
              <SummaryModal
                onCloseModal={onCloseModal}
                deploymentUuid={selectedDeploymentUuid}
              />
            ) : isModalOpen === ModalType.PREVIEW ? (
              <PreviewModal onCloseModal={onCloseModal} />
            ) : isModalOpen === ModalType.REDEPLOY ? (
              <RedeployModal onCloseModal={onCloseModal} />
            ) : null}
          </IntlProvider>
        </GridColumn>
      </Grid>
    </ContextualAnalyticsData>
  );
};

export default React.memo(DeploymentsPage);
